// RFC 4180 CSV reader for the translations importer.
//
// Hand-rolled rather than a dependency because the requirement is narrow and
// the failure mode is expensive: this must round-trip our own export, which is
// BOM-prefixed UTF-8 with Arabic values that routinely contain commas. A parser
// that splits on ',' corrupts those silently — the row still imports, just with
// half a sentence in it.

export interface CsvParseError {
  /** 1-based line number as a human counts them in Excel. */
  line: number;
  message: string;
}

export interface TranslationCsvRow {
  translationKey: string;
  languageCode: string;
  translationValue: string;
  category: string | null;
}

export interface TranslationCsvResult {
  rows: TranslationCsvRow[];
  errors: CsvParseError[];
}

const REQUIRED_HEADERS = ['key', 'language', 'value'] as const;

/**
 * Split CSV text into a grid of cells.
 *
 * Handles: quoted cells, commas and newlines inside quotes, `""` as a literal
 * quote, CRLF and LF line endings, and a leading UTF-8 BOM.
 */
export function parseCsv(text: string): string[][] {
  // Excel writes the BOM back out; left in place it becomes part of the first
  // header name and the header check fails for a file we ourselves produced.
  const input = text.replace(/^\uFEFF/, '');

  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;

  for (let i = 0; i < input.length; i += 1) {
    const char = input[i]!;

    if (inQuotes) {
      if (char === '"') {
        if (input[i + 1] === '"') {
          cell += '"'; // escaped quote
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        cell += char;
      }
      continue;
    }

    if (char === '"') {
      inQuotes = true;
    } else if (char === ',') {
      row.push(cell);
      cell = '';
    } else if (char === '\n' || char === '\r') {
      // Swallow the LF of a CRLF pair so it doesn't open an empty row.
      if (char === '\r' && input[i + 1] === '\n') i += 1;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
    } else {
      cell += char;
    }
  }

  // Trailing cell/row, unless the file ended on a newline.
  if (cell !== '' || row.length > 0) {
    row.push(cell);
    rows.push(row);
  }

  // Drop rows that are entirely empty (a trailing blank line is normal).
  return rows.filter((r) => !(r.length === 1 && r[0] === ''));
}

/**
 * Parse a translations CSV into API-ready rows.
 *
 * Expects a header row containing at least `key`, `language`, `value`;
 * `category` is optional. Column ORDER is taken from the header rather than
 * assumed, so a file with reordered columns still imports correctly.
 *
 * Bad rows are collected in `errors` rather than aborting — one malformed line
 * in a 700-row export shouldn't cost the editor the other 699.
 */
export function parseTranslationsCsv(text: string): TranslationCsvResult {
  const grid = parseCsv(text);
  const errors: CsvParseError[] = [];

  if (grid.length === 0) {
    return { rows: [], errors: [{ line: 1, message: 'The file is empty.' }] };
  }

  const header = grid[0]!.map((h) => h.trim().toLowerCase());
  const missing = REQUIRED_HEADERS.filter((h) => !header.includes(h));
  if (missing.length > 0) {
    return {
      rows: [],
      errors: [
        {
          line: 1,
          message: `Missing required column${missing.length > 1 ? 's' : ''}: ${missing.join(', ')}. Expected a header row of key,language,value,category.`,
        },
      ],
    };
  }

  const keyAt = header.indexOf('key');
  const langAt = header.indexOf('language');
  const valueAt = header.indexOf('value');
  const categoryAt = header.indexOf('category');

  const rows: TranslationCsvRow[] = [];

  for (let i = 1; i < grid.length; i += 1) {
    const cells = grid[i]!;
    const line = i + 1; // 1-based, header is line 1

    const translationKey = (cells[keyAt] ?? '').trim();
    const languageCode = (cells[langAt] ?? '').trim();
    // Value keeps its whitespace — leading spaces can be meaningful in a string.
    const translationValue = cells[valueAt] ?? '';
    const category = categoryAt >= 0 ? (cells[categoryAt] ?? '').trim() : '';

    if (translationKey === '' && languageCode === '' && translationValue === '') {
      continue; // blank row in the middle of the file
    }
    if (translationKey === '') {
      errors.push({ line, message: 'Missing key.' });
      continue;
    }
    if (languageCode === '') {
      errors.push({ line, message: `Missing language for "${translationKey}".` });
      continue;
    }
    // Mirrors the zod `translationKeyString` regex so a bad key is reported
    // here with a line number instead of as an opaque 400 from the API.
    if (!/^[A-Za-z0-9_]+(\.[A-Za-z0-9_]+)*$/.test(translationKey)) {
      errors.push({
        line,
        message: `"${translationKey}" is not a valid key (letters, digits, _ and . only).`,
      });
      continue;
    }

    rows.push({
      translationKey,
      languageCode,
      translationValue,
      category: category === '' ? null : category,
    });
  }

  return { rows, errors };
}
